<template>
    <section class="success-stories-list">
        <div class="slice-content">
            <div class="content">
                <PrismicRichText :field="slice.primary.title" class="title" />
                <PrismicRichText :field="slice.primary.headline" class="headline" />
            </div>
            <div class="success-stories">
                <div class="content">
                    <div class="success-stories-grid">
                        <ArticleCard v-for="successStory in successStories" :key="successStory.uid" :article="successStory" />
                    </div>
                    <div class="button-row">
                        <NuxtLink v-slot="{href, navigate}" :to="$withLocalePrefix('/resources/success-stories')" custom>
                            <VJoyButton variant="main" :href="href" @click="navigate">
                                {{ t('resources.successStories.seeAll') }}
                            </VJoyButton>
                        </NuxtLink>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {isFilled} from '@prismicio/helpers';
    import type {FilledContentRelationshipField} from '@prismicio/types';
    import {PrismicRichText} from '@prismicio/vue';
    import type {SuccessStoriesListSliceDefault, SuccessStoryDocument, SuccessStoryDocumentData} from '@prismicio-types';
    import {useAsyncData, useTranslation} from '#imports';
    import {VJoyButton} from '@maltjoy/core-vue';
    import ArticleCard from '~/components/ArticleCard.vue';
    import usePrismicSearch from '~/composables/content/usePrismicSearch';

    const props = defineProps<{slice: SuccessStoriesListSliceDefault}>();
    const successStories = [props.slice.primary.successStory1, props.slice.primary.successStory2, props.slice.primary.successStory3].filter(
        (successStory) => isFilled.contentRelationship<'successStory', string, SuccessStoryDocumentData>(successStory),
    ) as (FilledContentRelationshipField<'successStory', string, SuccessStoryDocumentData> | SuccessStoryDocument)[];

    const {t} = useTranslation();

    function fetchSuccessStories() {
        if (successStories.length < 3) {
            // prismic does not allow 'or' queries, so we have to do multiple queries
            const searchQueries = props.slice.items
                .filter((searchFilter) => searchFilter.categoryFilter)
                .map((item) =>
                    usePrismicSearch({
                        type: 'successStory',
                        pageSize: 3 - successStories.length,
                        category: item.categoryFilter ?? undefined,
                        excludes: successStories.map((successStory) => successStory.id),
                    }),
                );
            if (searchQueries.length === 0) {
                searchQueries.push(
                    usePrismicSearch({
                        type: 'successStory',
                        pageSize: 3 - successStories.length,
                        excludes: successStories.map((successStory) => successStory.id),
                    }),
                );
            }
            return Promise.all(searchQueries);
        }
        return Promise.resolve([]);
    }

    const {data: complementarySuccessStories} = await useAsyncData('complementary-success-stories', fetchSuccessStories);

    if (complementarySuccessStories.value) {
        const mostRecentComplementarySuccessStories = complementarySuccessStories.value
            .map((query) => query?.results)
            .flat()
            .filter((successStory) => successStory !== undefined)
            .sort((a, b) => new Date(b?.data?.releaseDate).getTime() - new Date(a?.data?.releaseDate).getTime())
            .slice(0, 3 - successStories.length) as SuccessStoryDocument[];
        successStories.push(...mostRecentComplementarySuccessStories);
    }
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .success-stories-list .slice-content {
        text-align: left;
    }

    .success-stories-list {
        position: relative;

        .success-stories {
            .success-stories-grid {
                display: grid;
                row-gap: var(--joy-core-spacing-5);
                grid-template-columns: minmax(0, 1fr);
                z-index: 1;
                position: relative;

                @include mq.screen_sm(min) {
                    grid-template-columns: repeat(2, minmax(0, 1fr));
                    gap: var(--joy-core-spacing-4) var(--joy-core-spacing-3);

                    & > :last-child {
                        grid-column: 1 / -1;
                        max-width: 50%;
                        margin: 0 auto;
                    }
                }

                @include mq.screen_lg(min) {
                    grid-template-columns: repeat(3, minmax(0, 1fr));
                    gap: var(--joy-core-spacing-6) var(--joy-core-spacing-4);
                    margin-bottom: 70px;

                    & > :last-child {
                        grid-column: auto;
                        max-width: 100%;
                    }
                }
            }
        }

        .content {
            .title p {
                text-align: center;
                color: var(--joy-color-primary-50);
                font-family: var(--joy-font-family-title);
                font-size: var(--joy-font-size-secondary-200);
                margin-bottom: var(--joy-core-spacing-5);
            }

            .headline {
                text-align: center;
                margin-bottom: var(--joy-core-spacing-11);
            }

            .button-row {
                display: flex;
                justify-content: center;
                margin-top: var(--joy-core-spacing-12);

                @include mq.screen_sm(min) {
                    margin-top: 80px;
                }
            }
        }
    }
</style>
